import {ChangeDetectionStrategy, Component, EventEmitter, Input, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslocoModule} from '@jsverse/transloco';
import {AnimalModel} from '../../models/animal.model';
import {AnimalMediaModel} from '../../models/animal-media.model';
import {AnimalMediaCategoryModel} from '../../models/animal-media-category.model';
import {SharedModule} from '../../../../shared/shared.module';
import {ButtonModule} from 'primeng/button';
import {DialogModule} from 'primeng/dialog';
import {AnimalImagesRepository} from '../../states/animal-images.repository';
import {AnimalVideoRepository} from '../../states/animal-video.repository';
import {Editor} from "primeng/editor";

@Component({
  selector: 'app-gallery-grid',
  templateUrl: './gallery-grid.component.html',
  styleUrls: ['./gallery-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule, FormsModule, TranslocoModule, SharedModule,
    ButtonModule, DialogModule, Editor
  ]
})
export class GalleryGridComponent {
  @Input() animal: AnimalModel | null = null;
  @Input() media: (AnimalMediaModel & { translatedCategories?: string; uiDate?: Date | null })[] = [];
  @Input() flippedItems: Record<number, boolean> = {};
  @Input() isMultiSelectMode = false;
  @Input() selectedIds: Set<number> = new Set<number>();
  @Input() mediaCategoryOptions: AnimalMediaCategoryModel[] = [];

  @Output() toggleSelect = new EventEmitter<number>();
  @Output() toggleFlip = new EventEmitter<{ id: number, event: MouseEvent }>();
  @Output() toggleSharedFolder = new EventEmitter<AnimalMediaModel>();
  @Output() toggleWebsite = new EventEmitter<AnimalMediaModel>();
  @Output() togglePreview = new EventEmitter<AnimalMediaModel>();
  @Output() categoryChange = new EventEmitter<AnimalMediaModel>();
  @Output() deleteMedia = new EventEmitter<AnimalMediaModel>();
  @Output() downloadMedia = new EventEmitter<AnimalMediaModel>();
  @Output() dateChange = new EventEmitter<AnimalMediaModel>();

  dialogVisible = false;
  dialogType: 'description' | 'externalDescription' | null = null;
  dialogItem: AnimalMediaModel | null = null;
  dialogValue: string = '';

  mediaDialogVisible = false;
  selectedMedia: (AnimalMediaModel & { translatedCategories?: string }) | null = null;

  constructor(
    private readonly animalImagesRepo: AnimalImagesRepository,
    private readonly animalVideoRepo: AnimalVideoRepository
  ) {
  }

  openDescriptionDialog(item: AnimalMediaModel, type: 'description' | 'externalDescription') {
    this.dialogType = type;
    this.dialogItem = item;
    this.dialogValue = type === 'description' ? (item.description ?? '') : (item.externalDescription ?? '');
    this.dialogVisible = true;
  }

  closeDialog() {
    this.dialogVisible = false;
    this.dialogType = null;
    this.dialogItem = null;
    this.dialogValue = '';
  }

  saveDescription() {
    if (!this.dialogItem || !this.dialogType) return;
    const update: Partial<AnimalMediaModel> = {};
    update[this.dialogType] = this.dialogValue;

    if (this.dialogItem.type === 'image') {
      this.animalImagesRepo.updateImage(this.dialogItem.id, update, this.dialogItem.animalId);
    } else if (this.dialogItem.type === 'video') {
      this.animalVideoRepo.updateVideo(this.dialogItem.id, update, this.dialogItem.animalId);
    }

    if (this.dialogType === 'description') {
      this.dialogItem.description = this.dialogValue;
    } else if (this.dialogType === 'externalDescription') {
      this.dialogItem.externalDescription = this.dialogValue;
    }

    this.media = [...this.media];

    this.closeDialog();
  }

  openMediaDialog(item: AnimalMediaModel & { translatedCategories?: string }, event: MouseEvent) {
    event.stopPropagation();
    this.selectedMedia = item;
    this.mediaDialogVisible = true;
  }

  closeMediaDialog() {
    this.mediaDialogVisible = false;
    this.selectedMedia = null;
  }

  isSelected(id: number): boolean {
    return this.selectedIds.has(id);
  }
}
